import React, { useEffect, useState } from 'react'

import {
    CircularProgress,
    IconButton,
    Modal,
    Paper,
    Slide,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
} from '@material-ui/core'

import { Container } from '../../../../components'
import { useService, useUser } from '../../../../hooks'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import { ProjectTable } from './ProjectTable'
import { initialProjectModalState, ProjectModalState } from './types'
import { BurnDownModal } from '../../../Scheduler_v2/Modals/BurnDownModal'
import { workorderContext } from '../../../../contexts'
import { SearchField } from '../../../../components/SearchField'
import { ProjectDetail } from './ProjectDetail'
import { Routes } from '../../../../navigation/config/routes'
import { resolveRoute } from '../../../../helpers'
import { Close } from '@material-ui/icons'
import { usePortfolioSchedule } from '../../../../hooks/usePortfolioSchedule'
import { PortfolioSchedule } from '../../../../models'
import { typographyStyle } from '../../../../theme/typographyStyle'

export const OrganizationProjectPage = () => {
    const {
        simplePortfolioScheduleList,
        portfolioScheduleDetail,
        portfolioScheduleLoadingState,
        getSimplePortfolioScheduleList,
        getPortfolioScheduleDetail,
    } = usePortfolioSchedule()

    const [modalState, setModalState] = useState<ProjectModalState>(
        initialProjectModalState,
    )

    const [searchText, setSearchText] = useState('')
    const [selectedTagId, setSelectedTagId] = useState<number | null>(null)

    const { userTags } = useUserTags()

    const { workorderList, loadingState } = workorderContext()

    const { organization } = useUser()

    const theme = useTheme()

    const { serviceList, getServiceList } = useService()

    useEffect(() => {
        getSimplePortfolioScheduleList({
            params: { organization: organization ?? undefined, active: true },
        }),
            getServiceList({})
    }, [organization])

    // Handle tag filter change
    const handleTagChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value
        setSelectedTagId(value === 'all' ? null : (value as number))
    }

    // Filter projects by selected tag
    const filteredScheduleList = React.useMemo(() => {
        if (!simplePortfolioScheduleList) return []
        if (selectedTagId === null) return simplePortfolioScheduleList

        const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
        if (!selectedTag) return simplePortfolioScheduleList

        return simplePortfolioScheduleList.filter((schedule) =>
            selectedTag.apartments.includes(schedule.apartment_id),
        )
    }, [simplePortfolioScheduleList, selectedTagId, userTags])

    const navigateToScheduleDetail = (scheduleDetail: PortfolioSchedule) => {
        const project = simplePortfolioScheduleList?.find(
            (project) => project.id === scheduleDetail.id,
        )

        let route = Routes.apartment.schedule.detail as string
        if (project && !project.active) {
            route = Routes.apartment.schedule.archived as string
        }
        const params = new URLSearchParams({
            apartment: project?.apartment_id.toString() ?? '',
            setup: 'false',
        }).toString()

        const url = `${resolveRoute(route, ':id', scheduleDetail.id)}?${params}`

        window.open(url, '_blank')
    }

    return (
        <Container style={{ flexDirection: 'column', flex: 1, height: '100%' }}>
            <Container
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginBottom: theme.spacing(1),
                }}
            >
                <span
                    style={{
                        ...typographyStyle(theme.typography.h4),
                        fontWeight: 700,
                        marginRight: theme.spacing(2),
                    }}
                >
                    Projects
                </span>

                <SearchField
                    placeholder="Search Projects"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />

                <FormControl
                    variant="outlined"
                    size="small"
                    style={{
                        minWidth: 180,
                        marginLeft: theme.spacing(2),
                        backgroundColor: theme.palette.background.default,
                    }}
                >
                    <InputLabel id="project-tag-label">Property Tag</InputLabel>
                    <Select
                        labelId="project-tag-label"
                        id="project-tag-select"
                        value={selectedTagId ?? 'all'}
                        onChange={handleTagChange}
                        label="Property Tag"
                    >
                        <MenuItem value="all">All Properties</MenuItem>
                        {userTags.map((tag) => (
                            <MenuItem key={tag.id} value={tag.id}>
                                <Box display="flex" alignItems="center">
                                    <Box
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: tag.color,
                                            marginRight: 8,
                                        }}
                                    />
                                    {tag.name}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Container>
            {portfolioScheduleLoadingState.portfolioScheduleList ? (
                <Container
                    style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                        height: '100%',
                    }}
                >
                    <CircularProgress size={100} />
                </Container>
            ) : (
                <ProjectTable
                    scheduleList={filteredScheduleList}
                    theme={theme}
                    searchText={searchText}
                    modalState={modalState}
                    setModalState={setModalState}
                    getPortfolioScheduleDetail={getPortfolioScheduleDetail}
                />
            )}

            <BurnDownModal
                open={modalState.burnDownModalOpen}
                theme={theme}
                workorders={workorderList ?? []}
                loading={loadingState.getWorkorderList}
                isWorkorderInFilter={() => true}
                onClose={() => {
                    setModalState(initialProjectModalState)
                }}
            />

            <Modal
                open={modalState.summaryReportModalOpen}
                onClose={() => {
                    setModalState(initialProjectModalState)
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={modalState.summaryReportModalOpen}>
                    <Paper
                        style={{
                            width: 'calc(100vw - 300px)',
                            padding: theme.spacing(2),
                            backgroundColor: theme.palette.background.paper,
                            height: 'calc(100vh - 200px)',
                        }}
                    >
                        <iframe
                            src={
                                modalState.selectedSchedule?.summary_report ??
                                ''
                            }
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                            }}
                            title="Summary Report"
                        />
                    </Paper>
                </Slide>
            </Modal>

            {modalState.selectedSchedule && (
                <Modal
                    open={modalState.detailModalOpen}
                    onClose={() => {
                        setModalState(initialProjectModalState)
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Slide direction="up" in={modalState.detailModalOpen}>
                        <Paper
                            style={{
                                width: 'calc(100vw - 250px)',
                                backgroundColor: theme.palette.background.paper,
                                height: 'calc(100vh - 150px)',
                            }}
                        >
                            <Container
                                style={{
                                    borderBottom: `1px solid ${theme.palette.grey[400]}`,
                                    padding: theme.spacing(1, 2),
                                    alignItems: 'center',
                                }}
                            >
                                <span
                                    style={{
                                        ...typographyStyle(theme.typography.h6),
                                    }}
                                >
                                    Project Detail
                                </span>
                                <Container style={{ flex: 1 }} />
                                <IconButton
                                    onClick={() => {
                                        setModalState(initialProjectModalState)
                                    }}
                                    size="small"
                                >
                                    <Close />
                                </IconButton>
                            </Container>
                            <ProjectDetail
                                scheduleDetail={portfolioScheduleDetail}
                                scheduleLoading={
                                    portfolioScheduleLoadingState.portfolioScheduleDetail
                                }
                                workorderList={workorderList ?? []}
                                workorderLoading={loadingState.getWorkorderList}
                                serviceList={serviceList ?? []}
                                navigateToScheduleDetail={
                                    navigateToScheduleDetail
                                }
                            />
                        </Paper>
                    </Slide>
                </Modal>
            )}
        </Container>
    )
}
